/**
 * workEvidenceModel — read-only read-model for the Work & Evidence Center.
 *
 * Builds one time-ordered, human-readable stream of work/evidence events from the records the
 * platform already owns: AI runs and their action audit rows, human approval tokens, tickets and
 * conversations (with employee handoffs). Nothing is invented: every event is anchored to a
 * timestamp that exists on its source record, records without any recorded time are counted as
 * unplaceable instead of being placed at a guessed time, and a proxy timestamp (e.g. updated_at
 * standing in for a missing finished_at) is always disclosed via `timeNote`.
 *
 * Every event may carry a verification state — verified / unverified / failed — so the operator
 * can tell proven success from claims and from failures:
 *   - verified:   for a run, audited verification — an executed close_ticket whose ticket is
 *                 currently closed, the same predicate employee_experience._verification_for
 *                 applies (basis `approved_close_executed`); for an action, a recorded outcome of
 *                 `succeeded`. A used approval token is NOT success, and neither is the actor
 *                 label a run carries in `context.technician` — that is whoever approved or
 *                 rejected the action (or "AI" on an auto-reject), and is never read as evidence.
 *   - unverified: a run reached a terminal success status without that verification being visible
 *                 on this surface. The experience/workspace surfaces additionally verify a run
 *                 from a technician RESOLUTION_OUTCOME note (basis
 *                 `technician_resolution_outcome`), which is an agent_memories row and not part of
 *                 the run payload this page loads — so the event states what it checked rather
 *                 than denying that any verification exists.
 *   - failed:     terminal non-success — failed or rejected, whether of the run or of an action.
 * Events with no state claim (started, proposals, approvals, tickets, conversations) carry null
 * and are only shown when no state filter is active.
 *
 * Display-only. Raw machine payloads (plan/analysis JSON, request/response, nonces, message
 * technical traces) never enter this module — approval tokens are already non-secret by the API
 * contract and only bounded human-readable text fields are read.
 */
import { parseUtc } from "../../utils/utcDate";
import { ACTION_TYPE_LABEL } from "../actionTypeVocabulary";
import { runStatusLabel } from "../runStatusVocabulary";
import { riskGradeLabel } from "../riskGradeVocabulary";
import { conversationStatusLabel } from "../conversationStatusVocabulary";
import { ticketStatusLabel } from "../ticketStatusVocabulary";

export const RUNS_LIMIT = 200;
export const TICKETS_LIMIT = 500;
export const CONVERSATIONS_LIMIT = 200;
// Approval tokens are fetched per run; cap the fan-out to the most recent runs.
export const APPROVAL_RUN_CAP = 25;
export const MAX_RENDERED_EVENTS = 400;

export const EVENT_TYPES = ["run", "action", "approval", "outcome", "ticket", "conversation"];

export const EVENT_TYPE_META = {
  run:          { label: "ריצות AI",       chip: "bg-blue-100 text-blue-700 border-blue-200" },
  action:       { label: "פעולות",         chip: "bg-amber-100 text-amber-700 border-amber-200" },
  approval:     { label: "אישורים",        chip: "bg-green-100 text-green-700 border-green-200" },
  outcome:      { label: "תוצאות ואימות",  chip: "bg-teal-100 text-teal-700 border-teal-200" },
  ticket:       { label: "קריאות",         chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  conversation: { label: "שיחות",          chip: "bg-violet-100 text-violet-700 border-violet-200" },
};

export const WORK_STATES = ["verified", "unverified", "failed"];

export const WORK_STATE_META = {
  verified:   { label: "מאומת",     chip: "bg-green-100 text-green-700 border-green-200" },
  unverified: { label: "לא אומת",   chip: "bg-amber-100 text-amber-700 border-amber-200" },
  failed:     { label: "נכשל/נדחה", chip: "bg-red-100 text-red-700 border-red-200" },
};

export const WINDOW_OPTIONS = [
  { hours: 24,  label: "24 שעות" },
  { hours: 168, label: "7 ימים" },
  { hours: 720, label: "30 ימים" },
  { hours: 0,   label: "הכל" },
];
export const DEFAULT_WINDOW_HOURS = 168;

export const PRIORITY_HE = { low: "עדיפות נמוכה", medium: "עדיפות בינונית", high: "עדיפות גבוהה" };

export const RISK_HE = { green: "סיכון נמוך", amber: "סיכון בינוני", red: "סיכון גבוה" };

export const ACTION_OUTCOME_HE = {
  proposed:  "מוצע",
  approved:  "אושר",
  rejected:  "נדחה",
  succeeded: "הצליח",
  failed:    "נכשל",
};

export const APPROVAL_STATE_HE = { active: "בתוקף", used: "נוצל", expired: "פג תוקף", revoked: "בוטל", unknown: "מצב לא ידוע" };

export const VERIFIED_RUN_STATUSES = new Set(["resolved", "completed"]);

export function boundedText(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

const tsMs = (iso) => {
  if (!iso) return null;
  const ms = parseUtc(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
};

// The verified-outcome basis in the operator's language — one constant so the copy and the
// predicate below cannot drift apart.
export const VERIFIED_OUTCOME_BASIS_HE = "ראיית אימות: סגירת קריאה מאושרת בוצעה והקריאה סגורה";
// Stated on every unverified outcome so "unverified" reads as "not visible here", never as a
// denial of verification that another surface owns.
export const UNVERIFIED_OUTCOME_BASIS_HE =
  "ראיות אימות אחרות — כגון הערת פתרון של טכנאי — אינן נכללות במסך זה";

/**
 * The audited verification arm this surface can actually see: the same two fields
 * employee_experience._verification_for reads for its `approved_close_executed` basis, both of
 * which the run-list endpoint already sends (AgentRunResponse.context / .ticket_status). Mirrored
 * rather than re-derived — workEvidenceVerificationParity.test.js fails if the service's predicate
 * changes underneath this, or if either field stops being sent.
 *
 * `context.technician` is deliberately not consulted: the approve/reject path writes the acting
 * human's address there, and the auto-reject path writes "AI", so it is an actor label on every
 * path that produces it — never verification evidence.
 */
export function hasExecutedApprovedClose(run) {
  const execution = run?.context?.execution_result;
  if (!execution || typeof execution !== "object") return false;
  return (
    Boolean(execution.success)
    && Boolean(execution.executed)
    && execution.action_type === "close_ticket"
  );
}

// The single rule behind the run-level outcome state. buildWorkEvidenceEvents calls this instead of
// re-deriving it, so there is exactly one place to keep faithful to the service.
export function isVerifiedOutcome(run) {
  return (
    VERIFIED_RUN_STATUSES.has(run?.status)
    && hasExecutedApprovedClose(run)
    && run?.ticket_status === "closed"
  );
}

export function approvalTokenState(token, nowMs = Date.now()) {
  if (!token) return "unknown";
  if (token.revoked) return "revoked";
  if (token.used) return "used";
  const expiresMs = tsMs(token.expires_at);
  if (expiresMs == null) return "unknown";
  return expiresMs < nowMs ? "expired" : "active";
}

function shortId(id) {
  return typeof id === "string" && id.length > 8 ? id.slice(0, 8) : typeof id === "string" ? id : null;
}

export function runRefLabel(run) {
  if (!run) return null;
  const parts = [];
  const sid = shortId(run.id);
  if (sid) parts.push(`ריצה ${sid}`);
  if (run.ticket_number != null) parts.push(`קריאה #${run.ticket_number}`);
  const agent = boundedText(run.agent_name, 60);
  if (agent) parts.push(`עובד/ת: ${agent}`);
  const company = boundedText(run.ticket_company_name, 60);
  if (company) parts.push(`חברה: ${company}`);
  return parts.length ? parts.join(" · ") : null;
}

function makeEvent({ id, type, atMs, at, title, detail, chips, ref, link, secondaryLink, timeNote, state, stateLabel, employeeIds }) {
  const ids = Array.isArray(employeeIds)
    ? [...new Set(employeeIds.filter(Boolean).map((value) => String(value)))]
    : [];
  const searchBlob = [title, detail, ref, link?.label, secondaryLink?.label, stateLabel, ...(chips ?? []).map((c) => c.label)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return {
    id,
    type,
    atMs,
    at,
    title,
    detail: detail ?? null,
    chips: chips ?? [],
    ref: ref ?? null,
    link: link ?? null,
    secondaryLink: secondaryLink ?? null,
    timeNote: timeNote ?? null,
    state: state ?? null,
    stateLabel: stateLabel ?? null,
    employeeIds: ids,
    searchBlob,
  };
}

/**
 * Build the unified work/evidence event stream.
 * Returns { events, undated } where `undated` counts source records that carry no recorded
 * timestamp and therefore were not placed on the timeline.
 */
export function buildWorkEvidenceEvents({
  runs = [],
  tickets = [],
  approvals = null,
  conversations = [],
  nowMs = Date.now(),
} = {}) {
  const events = [];
  const undated = { run: 0, ticket: 0, action: 0, approval: 0, conversation: 0 };

  const runsList = Array.isArray(runs) ? runs.filter(Boolean) : [];
  const ticketsList = Array.isArray(tickets) ? tickets.filter(Boolean) : [];
  const conversationsList = Array.isArray(conversations) ? conversations.filter(Boolean) : [];
  const runsById = new Map(runsList.filter((r) => r?.id).map((r) => [String(r.id), r]));

  // Employees who worked a ticket (via their runs) — lets the employee lens cover ticket events.
  const agentIdsByTicket = new Map();
  for (const run of runsList) {
    if (!run?.ticket_id || !run?.agent_id) continue;
    const key = String(run.ticket_id);
    if (!agentIdsByTicket.has(key)) agentIdsByTicket.set(key, new Set());
    agentIdsByTicket.get(key).add(String(run.agent_id));
  }

  for (const run of runsList) {
    const ref = runRefLabel(run);
    const link = run?.id ? { to: `/agent-runs/${run.id}`, label: "פתח ריצה" } : null;
    const ticketLink = run?.ticket_id ? { to: `/tickets/${run.ticket_id}`, label: "פתח קריאה" } : null;
    const employeeIds = run?.agent_id ? [run.agent_id] : [];
    const startedMs = tsMs(run.created_at);
    const finishedMs = tsMs(run.finished_at);
    const updatedMs = tsMs(run.updated_at);
    const statusLabel = runStatusLabel(run.status);

    if (startedMs != null) {
      events.push(makeEvent({
        id: `run-${run.id}-started`,
        type: "run",
        atMs: startedMs,
        at: run.created_at,
        title: "הרצת AI החלה",
        detail: boundedText(run.input_summary, 220) ?? boundedText(run.ticket_title, 160),
        chips: statusLabel && finishedMs == null ? [{ label: `מצב נוכחי: ${statusLabel}` }] : [],
        ref,
        link,
        secondaryLink: ticketLink,
        employeeIds,
      }));
    } else {
      undated.run += 1;
    }

    const endProxyNote = "מועד הסיום לא תועד — מוצג מועד העדכון האחרון";

    if (run.status === "failed") {
      const atMs = finishedMs ?? updatedMs;
      if (atMs != null) {
        events.push(makeEvent({
          id: `run-${run.id}-failed`,
          type: "run",
          atMs,
          at: run.finished_at ?? run.updated_at,
          title: "הריצה נכשלה",
          detail: boundedText(run.result_summary, 220),
          chips: statusLabel ? [{ label: statusLabel }] : [],
          ref,
          link,
          secondaryLink: ticketLink,
          timeNote: finishedMs == null ? "מועד הכשל לא תועד — מוצג מועד העדכון האחרון" : null,
          state: "failed",
          stateLabel: "נכשלה",
          employeeIds,
        }));
      }
    } else if (run.status === "rejected") {
      const atMs = finishedMs ?? updatedMs;
      if (atMs != null) {
        events.push(makeEvent({
          id: `run-${run.id}-rejected`,
          type: "run",
          atMs,
          at: run.finished_at ?? run.updated_at,
          title: "הריצה נדחתה — הפעולה לא בוצעה",
          detail: boundedText(run.result_summary, 220),
          chips: statusLabel ? [{ label: statusLabel }] : [],
          ref,
          link,
          secondaryLink: ticketLink,
          timeNote: finishedMs == null ? "מועד הדחייה לא תועד — מוצג מועד העדכון האחרון" : null,
          state: "failed",
          stateLabel: "נדחתה",
          employeeIds,
        }));
      }
    } else if (VERIFIED_RUN_STATUSES.has(run.status)) {
      const atMs = finishedMs ?? updatedMs;
      if (atMs != null) {
        const summary = boundedText(run.result_summary, 200);
        if (isVerifiedOutcome(run)) {
          events.push(makeEvent({
            id: `run-${run.id}-verified`,
            type: "outcome",
            atMs,
            at: run.finished_at ?? run.updated_at,
            title: "תוצאה מאומתת — סגירת קריאה מאושרת בוצעה",
            detail: [VERIFIED_OUTCOME_BASIS_HE, summary].filter(Boolean).join(" · ") || null,
            chips: statusLabel ? [{ label: statusLabel }] : [],
            ref,
            link,
            secondaryLink: ticketLink,
            timeNote: finishedMs == null ? endProxyNote : null,
            state: "verified",
            stateLabel: "מאומת",
            employeeIds,
          }));
        } else {
          events.push(makeEvent({
            id: `run-${run.id}-unverified`,
            type: "outcome",
            atMs,
            at: run.finished_at ?? run.updated_at,
            title: "הסתיימה ללא ראיית אימות במסך זה",
            detail: [
              `הריצה הסתיימה בסטטוס "${statusLabel ?? run.status}" אך לא נמצאה בה סגירת קריאה מאושרת שבוצעה`,
              UNVERIFIED_OUTCOME_BASIS_HE,
              summary,
            ].filter(Boolean).join(" · "),
            chips: [],
            ref,
            link,
            secondaryLink: ticketLink,
            timeNote: finishedMs == null ? endProxyNote : null,
            state: "unverified",
            stateLabel: "לא אומת",
            employeeIds,
          }));
        }
      }
    } else if (finishedMs != null) {
      events.push(makeEvent({
        id: `run-${run.id}-finished`,
        type: "run",
        atMs: finishedMs,
        at: run.finished_at,
        title: "הרצת AI הסתיימה",
        detail: boundedText(run.result_summary, 220),
        chips: statusLabel ? [{ label: statusLabel }] : [],
        ref,
        link,
        secondaryLink: ticketLink,
        employeeIds,
      }));
    }

    const actions = Array.isArray(run.actions) ? run.actions.filter(Boolean) : [];
    for (const action of actions) {
      const createdMs = tsMs(action.created_at);
      const executedMs = tsMs(action.executed_at);
      const description = boundedText(action.description, 220);
      const baseChips = [];
      const typeLabel = ACTION_TYPE_LABEL[action.action_type] ?? boundedText(String(action.action_type ?? ""), 60);
      if (typeLabel) baseChips.push({ label: typeLabel });
      if (action.risk_level && RISK_HE[action.risk_level]) baseChips.push({ label: RISK_HE[action.risk_level] });
      const chipsFor = (outcome) => {
        const label = ACTION_OUTCOME_HE[outcome];
        return label ? [...baseChips, { label }] : baseChips;
      };
      const executed = action.outcome === "succeeded" || action.outcome === "failed";
      // A rejected proposal / executed-without-recorded-time action still has a real outcome but
      // only one recorded timestamp (the proposal) — state is shown there with a timeNote.
      const proposalState = executed
        ? executedMs == null
          ? (action.outcome === "succeeded" ? "verified" : "failed")
          : null
        : action.outcome === "rejected" ? "failed" : null;
      const proposalStateLabel =
        proposalState == null ? null
        : action.outcome === "rejected" ? "נדחתה"
        : action.outcome === "succeeded" ? "בוצעה"
        : "נכשלה";

      if (createdMs != null) {
        events.push(makeEvent({
          id: `action-${action.id}-proposed`,
          type: "action",
          atMs: createdMs,
          at: action.created_at,
          title: "הוצעה פעולה",
          detail: description,
          chips: executed ? baseChips : chipsFor(action.outcome),
          ref,
          link,
          secondaryLink: ticketLink,
          timeNote: executed && executedMs == null ? "מועד הביצוע לא תועד — מוצג מועד הצעת הפעולה" : null,
          state: proposalState,
          stateLabel: proposalStateLabel,
          employeeIds,
        }));
      } else if (executedMs == null) {
        undated.action += 1;
      }

      if (executedMs != null && executed) {
        events.push(makeEvent({
          id: `action-${action.id}-executed`,
          type: "action",
          atMs: executedMs,
          at: action.executed_at,
          title: action.outcome === "succeeded" ? "בוצעה פעולה" : "פעולה נכשלה",
          detail: description,
          chips: chipsFor(action.outcome),
          ref,
          link,
          secondaryLink: ticketLink,
          state: action.outcome === "succeeded" ? "verified" : "failed",
          stateLabel: action.outcome === "succeeded" ? "בוצעה ואומתה" : "נכשלה",
          employeeIds,
        }));
      }
    }
  }

  const tokensByRun = approvals?.tokensByRun ?? {};
  for (const runId of Object.keys(tokensByRun)) {
    const tokens = Array.isArray(tokensByRun[runId]) ? tokensByRun[runId].filter(Boolean) : [];
    for (const token of tokens) {
      const approvedAt = token.approved_at;
      const at = approvedAt ?? token.created_at;
      const atMs = tsMs(at);
      if (atMs == null) {
        undated.approval += 1;
        continue;
      }
      const run = runsById.get(String(runId));
      const chips = [];
      chips.push({ label: `סיכון: ${riskGradeLabel(token.risk_level)}` });
      chips.push({ label: `מצב: ${APPROVAL_STATE_HE[approvalTokenState(token, nowMs)]}` });
      const detailParts = [];
      const actionLabel = ACTION_TYPE_LABEL[token.action_type] ?? boundedText(token.action_type, 60);
      if (actionLabel) detailParts.push(actionLabel);
      if (token.approver_id) detailParts.push(`מאשר: ${boundedText(String(token.approver_id), 80)}`);
      if (token.target) detailParts.push(`יעד: ${boundedText(String(token.target), 120)}`);
      events.push(makeEvent({
        id: `approval-${token.id}`,
        type: "approval",
        atMs,
        at,
        title: "אישור אנושי נרשם",
        detail: detailParts.join(" · ") || null,
        chips,
        ref: run ? runRefLabel(run) : null,
        link: runId ? { to: `/agent-runs/${runId}`, label: "פתח ריצה" } : null,
        secondaryLink: run?.ticket_id ? { to: `/tickets/${run.ticket_id}`, label: "פתח קריאה" } : null,
        timeNote: approvedAt == null ? "מועד ההחלטה לא תועד — מוצג מועד יצירת האישור" : null,
        employeeIds: run?.agent_id ? [run.agent_id] : [],
      }));
    }
  }

  for (const ticket of ticketsList) {
    const openedAt = ticket.opened_at ?? ticket.created_at;
    const openedMs = tsMs(openedAt);
    const closedMs = tsMs(ticket.closed_at);
    const updatedMs = tsMs(ticket.updated_at);
    const number = ticket.ticket_number != null ? `#${ticket.ticket_number}` : "";
    const title = boundedText(ticket.title, 160);
    const link = ticket.id ? { to: `/tickets/${ticket.id}`, label: "פתח קריאה" } : null;
    const sender = boundedText(ticket.sender_name, 80);
    const ref = `קריאה ${number}`.trim() || null;
    const employeeIds = ticket.id ? [...(agentIdsByTicket.get(String(ticket.id)) ?? [])] : [];
    const priorityChip = PRIORITY_HE[ticket.priority] ? [{ label: PRIORITY_HE[ticket.priority] }] : [];
    const openedChips = ticket.is_sla_breach === true ? [...priorityChip, { label: "חריגת SLA" }] : priorityChip;

    if (openedMs != null) {
      events.push(makeEvent({
        id: `ticket-${ticket.id}-opened`,
        type: "ticket",
        atMs: openedMs,
        at: openedAt,
        title: `נפתחה קריאה ${number}`.trim(),
        detail: [title, sender ? `מדווח: ${sender}` : null].filter(Boolean).join(" · ") || null,
        chips: openedChips,
        ref,
        link,
        employeeIds,
      }));
    } else {
      undated.ticket += 1;
    }

    if (closedMs != null) {
      events.push(makeEvent({
        id: `ticket-${ticket.id}-closed`,
        type: "ticket",
        atMs: closedMs,
        at: ticket.closed_at,
        title: `נסגרה קריאה ${number}`.trim(),
        detail: title,
        chips: [],
        ref,
        link,
        employeeIds,
      }));
    }

    const lastKnownMs = closedMs ?? openedMs;
    if (updatedMs != null && lastKnownMs != null && updatedMs > lastKnownMs + 60_000) {
      events.push(makeEvent({
        id: `ticket-${ticket.id}-updated`,
        type: "ticket",
        atMs: updatedMs,
        at: ticket.updated_at,
        title: `עודכנה קריאה ${number}`.trim(),
        detail: [
          "השינוי האחרון שתועד בקריאה",
          ticket.status ? `מצב נוכחי: ${ticketStatusLabel(ticket.status)}` : null,
        ].filter(Boolean).join(" · "),
        chips: [],
        ref,
        link,
        employeeIds,
      }));
    }
  }

  for (const convo of conversationsList) {
    const createdMs = tsMs(convo.created_at);
    const updatedMs = tsMs(convo.updated_at);
    const closedMs = tsMs(convo.closed_at);
    const link = convo.id ? { to: `/conversations/${convo.id}`, label: "פתח שיחה" } : null;
    const ticketLink = convo.ticket_id ? { to: `/tickets/${convo.ticket_id}`, label: "פתח קריאה" } : null;
    const participants = Array.isArray(convo.participants) ? convo.participants.filter(Boolean) : [];
    const agentParticipants = participants.filter((p) => p.role === "agent" && p.agent_id);
    const employeeIds = [...new Set(agentParticipants.map((p) => String(p.agent_id)))];
    const agentNames = [...new Set(agentParticipants.map((p) => boundedText(p.agent_name, 60)).filter(Boolean))];
    const ref = agentNames.length ? `שיחה · ${agentNames.join(", ")}` : convo.id ? `שיחה ${shortId(convo.id)}` : null;
    const statusChip = [{ label: `מצב: ${conversationStatusLabel(convo.status)}` }];

    if (createdMs != null) {
      events.push(makeEvent({
        id: `conv-${convo.id}-opened`,
        type: "conversation",
        atMs: createdMs,
        at: convo.created_at,
        title: "נפתחה שיחה",
        detail: null,
        chips: statusChip,
        ref,
        link,
        secondaryLink: ticketLink,
        employeeIds,
      }));
    } else {
      undated.conversation += 1;
    }

    if (closedMs != null) {
      events.push(makeEvent({
        id: `conv-${convo.id}-closed`,
        type: "conversation",
        atMs: closedMs,
        at: convo.closed_at,
        title: "נסגרה שיחה",
        detail: null,
        chips: statusChip,
        ref,
        link,
        secondaryLink: ticketLink,
        employeeIds,
      }));
    }

    const lastKnownMs = closedMs ?? createdMs;
    if (updatedMs != null && lastKnownMs != null && updatedMs > lastKnownMs + 60_000) {
      events.push(makeEvent({
        id: `conv-${convo.id}-updated`,
        type: "conversation",
        atMs: updatedMs,
        at: convo.updated_at,
        title: "עודכנה שיחה",
        detail: "השינוי האחרון שתועד בשיחה",
        chips: statusChip,
        ref,
        link,
        secondaryLink: ticketLink,
        employeeIds,
      }));
    }

    for (const participant of agentParticipants) {
      if (participant.is_active !== false) continue;
      const leftMs = tsMs(participant.left_at);
      if (leftMs == null) {
        undated.conversation += 1;
        continue;
      }
      const name = boundedText(participant.agent_name, 60);
      const reason = boundedText(participant.handoff_reason, 160);
      events.push(makeEvent({
        id: `conv-${convo.id}-handoff-${participant.id}`,
        type: "conversation",
        atMs: leftMs,
        at: participant.left_at,
        title: "העברה בין עובדים — העובד/ת סיים/ה את ההשתתפות",
        detail: [name ? `עובד/ת: ${name}` : null, reason ? `סיבה: ${reason}` : null].filter(Boolean).join(" · ") || null,
        chips: [],
        ref,
        link,
        secondaryLink: ticketLink,
        employeeIds: [participant.agent_id],
      }));
    }
  }

  events.sort((a, b) => (b.atMs - a.atMs) || String(a.id).localeCompare(String(b.id)));
  return { events, undated };
}

export function filterWorkEvidenceEvents(events, { types, states, windowHours, query, employeeId, nowMs = Date.now() } = {}) {
  const activeTypes = types instanceof Set ? types : new Set(Array.isArray(types) ? types : EVENT_TYPES);
  const activeStates = states instanceof Set ? states : new Set(Array.isArray(states) ? states : []);
  const tokens = (query ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  const minMs = Number.isFinite(windowHours) && windowHours > 0 ? nowMs - windowHours * 3_600_000 : null;
  const employeeKey = employeeId != null && employeeId !== "" ? String(employeeId) : null;
  return (Array.isArray(events) ? events : []).filter((event) => {
    if (!activeTypes.has(event.type)) return false;
    if (activeStates.size > 0 && !(event.state && activeStates.has(event.state))) return false;
    if (minMs != null && event.atMs < minMs) return false;
    if (employeeKey != null && !event.employeeIds.includes(employeeKey)) return false;
    if (tokens.length && !tokens.every((token) => event.searchBlob.includes(token))) return false;
    return true;
  });
}

export function countsByType(events) {
  const counts = Object.fromEntries(EVENT_TYPES.map((type) => [type, 0]));
  for (const event of Array.isArray(events) ? events : []) {
    if (counts[event.type] != null) counts[event.type] += 1;
  }
  return counts;
}

export function countsByState(events) {
  const counts = { verified: 0, unverified: 0, failed: 0, none: 0 };
  for (const event of Array.isArray(events) ? events : []) {
    if (event.state && counts[event.state] != null) counts[event.state] += 1;
    else counts.none += 1;
  }
  return counts;
}

const DAY_LABEL_FMT = new Intl.DateTimeFormat("he-IL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const CLOCK_FMT = new Intl.DateTimeFormat("he-IL", { hour: "2-digit", minute: "2-digit" });
const FULL_FMT = new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" });

export function formatEventClock(atMs) {
  return Number.isFinite(atMs) ? CLOCK_FMT.format(atMs) : "מועד לא תועד";
}

export function formatEventFull(atMs) {
  return Number.isFinite(atMs) ? FULL_FMT.format(atMs) : "מועד לא תועד";
}

function localDayKey(atMs) {
  const d = new Date(atMs);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function groupEventsByDay(events, nowMs = Date.now()) {
  const todayKey = localDayKey(nowMs);
  const yesterdayKey = localDayKey(nowMs - 24 * 3_600_000);
  const groups = [];
  const byKey = new Map();
  for (const event of Array.isArray(events) ? events : []) {
    const key = localDayKey(event.atMs);
    let group = byKey.get(key);
    if (!group) {
      const prefix = key === todayKey ? "היום" : key === yesterdayKey ? "אתמול" : null;
      const dateLabel = DAY_LABEL_FMT.format(event.atMs);
      group = { key, label: prefix ? `${prefix} · ${dateLabel}` : dateLabel, events: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.events.push(event);
  }
  return groups;
}
